/**
 * Interface translation runtime.
 *
 * English is the source of truth and lives in src/locales/en/<namespace>.json
 * (bundled). For any other language the provider asks the backend to translate
 * each namespace's strings (POST /i18n/ui), which serves them from its cache
 * or machine-translates them once via Amazon Translate. Results are cached in
 * localStorage per (locale, namespace, hash of the English file), so a change
 * to the English copy refreshes just that namespace.
 *
 * Usage:
 *   const { t } = useTranslation("common");
 *   t("actions.save")                        -> "Save"
 *   t("points", { count: n })                -> picks "points_one" / "points_other"
 *   t("feed:feed.greeting", { name })        -> "Hello {{name}}" interpolated
 *
 * Until a translation arrives the English text renders, so nothing blocks.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "../services/api";
import AuthContext from "../context/AuthContext";
import { getCurrentLocale, isCurrentLocaleRtl } from "../utils/locale";

// ---------------------------------------------------------------------------
// English catalogs (bundled)
// ---------------------------------------------------------------------------

const flatten = (node, prefix = "", out = {}) => {
  if (node && typeof node === "object" && !Array.isArray(node)) {
    Object.entries(node).forEach(([k, v]) => {
      flatten(v, prefix ? `${prefix}.${k}` : k, out);
    });
  } else if (prefix) {
    out[prefix] = String(node);
  }
  return out;
};

const loadEnglish = () => {
  const catalogs = {};
  try {
    // Webpack (CRA) picks up every JSON file in src/locales/en at build time.
    const ctx = require.context("../locales/en", false, /\.json$/);
    ctx.keys().forEach((file) => {
      const ns = file.replace(/^\.\//, "").replace(/\.json$/, "");
      catalogs[ns] = flatten(ctx(file));
    });
  } catch {
    // No catalogs yet; t() falls back to keys.
  }
  return catalogs;
};

const EN = loadEnglish();
export const NAMESPACES = Object.keys(EN);

// Cheap stable hash so a changed English file invalidates the cached copy.
const hashOf = (obj) => {
  const s = JSON.stringify(obj);
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
};
const EN_HASH = Object.fromEntries(
  Object.entries(EN).map(([ns, strings]) => [ns, hashOf(strings)])
);

const STORAGE_PREFIX = "i18n:";
const readCache = (locale, ns) => {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${locale}:${ns}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.hash === EN_HASH[ns] ? parsed.translations : null;
  } catch {
    return null;
  }
};
const writeCache = (locale, ns, translations) => {
  try {
    localStorage.setItem(
      `${STORAGE_PREFIX}${locale}:${ns}`,
      JSON.stringify({ hash: EN_HASH[ns], translations })
    );
  } catch {
    // Storage full or unavailable; the in-memory copy still works.
  }
};

const MAX_PER_CALL = 500;
const chunk = (entries) => {
  const out = [];
  for (let i = 0; i < entries.length; i += MAX_PER_CALL) {
    out.push(entries.slice(i, i + MAX_PER_CALL));
  }
  return out;
};

const fetchNamespace = async (locale, ns) => {
  const strings = EN[ns] || {};
  const parts = chunk(Object.entries(strings));
  const merged = {};
  for (const part of parts) {
    const { data } = await api.post("/api/v1/i18n/ui", {
      locale,
      strings: Object.fromEntries(part),
    });
    Object.assign(merged, (data && data.translations) || {});
  }
  return merged;
};

// ---------------------------------------------------------------------------
// Interpolation and plural rules
// ---------------------------------------------------------------------------

const interpolate = (template, vars) => {
  if (!vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, name) =>
    vars[name] === undefined || vars[name] === null ? m : String(vars[name])
  );
};

const pluralSuffix = (locale, count) => {
  try {
    return new Intl.PluralRules(locale).select(count);
  } catch {
    return count === 1 ? "one" : "other";
  }
};

const lookup = (catalog, fallback, key, vars, locale) => {
  const tryKeys = [];
  if (vars && typeof vars.count === "number") {
    tryKeys.push(`${key}_${pluralSuffix(locale, vars.count)}`);
    tryKeys.push(`${key}_other`);
  }
  tryKeys.push(key);
  for (const k of tryKeys) {
    if (catalog && catalog[k] !== undefined) return catalog[k];
  }
  for (const k of tryKeys) {
    if (fallback && fallback[k] !== undefined) return fallback[k];
  }
  return null;
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const I18nContext = createContext({
  locale: "en",
  rtl: false,
  catalogs: {},
  translationEnabled: false,
  ready: true,
});

export const I18nProvider = ({ locale: localeProp, rtl: rtlProp, children }) => {
  const locale = localeProp || "en";
  const rtl = !!rtlProp;
  const [catalogs, setCatalogs] = useState({});
  const [ready, setReady] = useState(locale === "en");
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const inflight = useRef(new Set());

  // Load every namespace for the active locale: cache first, then backend.
  useEffect(() => {
    let cancelled = false;
    if (locale === "en") {
      setCatalogs({});
      setReady(true);
      return undefined;
    }
    const fromCache = {};
    const toFetch = [];
    NAMESPACES.forEach((ns) => {
      const cached = readCache(locale, ns);
      if (cached) fromCache[ns] = cached;
      else toFetch.push(ns);
    });
    setCatalogs({ [locale]: fromCache });
    setReady(toFetch.length === 0);

    const hasToken =
      (() => {
        try {
          return !!(localStorage.getItem("token") || sessionStorage.getItem("token"));
        } catch {
          return false;
        }
      })();
    if (!hasToken || toFetch.length === 0) {
      setReady(true);
      return undefined;
    }

    Promise.all(
      toFetch.map(async (ns) => {
        const tag = `${locale}:${ns}`;
        if (inflight.current.has(tag)) return;
        inflight.current.add(tag);
        try {
          const translations = await fetchNamespace(locale, ns);
          if (cancelled) return;
          writeCache(locale, ns, translations);
          setCatalogs((prev) => ({
            ...prev,
            [locale]: { ...(prev[locale] || {}), [ns]: translations },
          }));
        } catch {
          // Leave English for this namespace.
        } finally {
          inflight.current.delete(tag);
        }
      })
    ).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  // Whether a translation engine is configured (drives Translate buttons).
  useEffect(() => {
    let cancelled = false;
    const hasToken = (() => {
      try {
        return !!(localStorage.getItem("token") || sessionStorage.getItem("token"));
      } catch {
        return false;
      }
    })();
    if (!hasToken) {
      setTranslationEnabled(false);
      return undefined;
    }
    api
      .get("/api/v1/i18n/locale")
      .then(({ data }) => {
        if (!cancelled) setTranslationEnabled(!!(data && data.translation_enabled));
      })
      .catch(() => {
        if (!cancelled) setTranslationEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [locale]);

  const value = useMemo(
    () => ({ locale, rtl, catalogs: catalogs[locale] || {}, translationEnabled, ready }),
    [locale, rtl, catalogs, translationEnabled, ready]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

/** Mounts I18nProvider with the locale the backend resolved for the user. */
export const I18nFromAuth = ({ children }) => {
  const { user } = useContext(AuthContext) || {};
  const locale = user?.effective_locale || getCurrentLocale() || "en";
  const rtl = user ? !!user.effective_locale_rtl : isCurrentLocaleRtl();
  return (
    <I18nProvider locale={locale} rtl={rtl}>
      {children}
    </I18nProvider>
  );
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const useLocale = () => {
  const { locale, rtl, translationEnabled, ready } = useContext(I18nContext);
  return { locale, rtl, translationEnabled, ready };
};

export const useTranslation = (namespace = "common") => {
  const { locale, rtl, catalogs, ready, translationEnabled } = useContext(I18nContext);
  const catalog = catalogs[namespace];
  const fallback = EN[namespace];

  const t = useCallback(
    (key, vars) => {
      const found = lookup(catalog, fallback, key, vars, locale);
      if (found === null) {
        // Allow "ns:key" to reach into another namespace.
        const idx = key.indexOf(":");
        if (idx > 0) {
          const ns = key.slice(0, idx);
          const k = key.slice(idx + 1);
          const other = lookup(catalogs[ns], EN[ns], k, vars, locale);
          if (other !== null) return interpolate(other, vars);
        }
        return interpolate(vars && vars.defaultValue ? vars.defaultValue : key, vars);
      }
      return interpolate(found, vars);
    },
    [catalog, fallback, catalogs, locale]
  );

  return { t, locale, rtl, ready, translationEnabled };
};

// ---------------------------------------------------------------------------
// Locale-aware formatting (Intl; no library needed)
// ---------------------------------------------------------------------------

const toDate = (value) => (value instanceof Date ? value : new Date(value));

export const formatDate = (value, locale, options) => {
  if (!value) return "";
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(locale, options || { dateStyle: "medium" }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
};

export const formatDateTime = (value, locale, options) =>
  formatDate(value, locale, options || { dateStyle: "medium", timeStyle: "short" });

export const formatNumber = (value, locale, options) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "";
  try {
    return new Intl.NumberFormat(locale, options).format(Number(value));
  } catch {
    return String(value);
  }
};

export const formatCurrency = (value, locale, currency = "USD", options) =>
  formatNumber(value, locale, { style: "currency", currency, ...(options || {}) });

export const formatRelative = (value, locale) => {
  if (!value) return "";
  const d = toDate(value);
  const diffSec = Math.round((d.getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  const units = [
    ["year", 31536000],
    ["month", 2592000],
    ["week", 604800],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    for (const [unit, secs] of units) {
      if (abs >= secs) return rtf.format(Math.round(diffSec / secs), unit);
    }
    return rtf.format(diffSec, "second");
  } catch {
    return d.toLocaleString();
  }
};

/** Formatting helpers bound to the active locale. */
export const useFormat = () => {
  const { locale } = useContext(I18nContext);
  return useMemo(
    () => ({
      locale,
      date: (v, o) => formatDate(v, locale, o),
      dateTime: (v, o) => formatDateTime(v, locale, o),
      number: (v, o) => formatNumber(v, locale, o),
      currency: (v, c, o) => formatCurrency(v, locale, c, o),
      relative: (v) => formatRelative(v, locale),
    }),
    [locale]
  );
};

export default I18nContext;
